import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Pencil, Trash2, Radio, RefreshCw, Loader2 } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import Card from "@/components/common/Card";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import ActionMenu from "@/components/common/ActionMenu";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import RowIcon from "@/components/common/RowIcon";
import { usePageTitle } from "@/components/common/AppShell";
import { createPixelsApi } from "../api";
import { formatCount, formatDateTime } from "../format";
import { createLimiter } from "../limiter";
import PixelForm from "../components/PixelForm";

const PAGE_SIZE = 25;
/** Summary requests in flight at once, per page. */
const SUMMARY_CONCURRENCY = 4;

/** One row's lazily loaded activity cell: spinner, value, or "—" + retry. */
function SummaryCell({ summary, render, onRetry, label }) {
  if (!summary || summary.state === "loading") {
    return (
      <Loader2
        size={14}
        className="animate-spin text-(--text-muted)"
        aria-label="Loading"
      />
    );
  }
  if (summary.state === "error") {
    return (
      <span className="inline-flex items-center gap-2">
        <span title={summary.error}>—</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRetry();
          }}
          aria-label={`Retry ${label}`}
          title="Retry"
          className="inline-flex items-center border-0 bg-transparent p-0 text-(--text-muted) hover:text-(--text-primary)"
        >
          <RefreshCw size={13} />
        </button>
      </span>
    );
  }
  return render(summary.data);
}

export default function PixelsPage() {
  usePageTitle("Pixels");
  const { performRequest, currentUserType, users } = useApp();
  const api = useMemo(() => createPixelsApi(performRequest), [performRequest]);
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.replace(/\/pixels.*$/, "");
  const isAdmin = currentUserType === "admin";

  const [owner, setOwner] = useState("");
  // Keyset pagination: cursors[i] is the cursor that loads page i.
  const [cursors, setCursors] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [page, setPage] = useState({ items: [], nextCursor: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [modal, setModal] = useState(null); // { pixel? } when open
  // Per-row activity, loaded after the table renders:
  // { [uuid]: { state: "loading" | "ok" | "error", data?, error? } }
  const [summaries, setSummaries] = useState({});
  const limiter = useRef(null);
  if (!limiter.current) limiter.current = createLimiter(SUMMARY_CONCURRENCY);
  // Bumped whenever the visible rows change; a response for an older
  // generation is dropped.
  const generation = useRef(0);

  const cursor = cursors[pageIndex];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .list({
        owner: isAdmin ? owner : "",
        limit: PAGE_SIZE,
        cursor,
      })
      .then((res) => {
        if (!cancelled) setPage(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setPage({ items: [], nextCursor: null });
          setError(err?.message || "Failed to load pixels");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, isAdmin, owner, cursor, refreshTick]);

  const loadSummary = useCallback(
    (uuid) => {
      const gen = generation.current;
      const settle = (value) => {
        if (gen === generation.current) {
          setSummaries((prev) => ({ ...prev, [uuid]: value }));
        }
      };
      settle({ state: "loading" });
      limiter.current
        .run(() =>
          // Rows of a page the user already left never send their request.
          gen === generation.current ? api.summary(uuid) : null,
        )
        .then((data) => settle({ state: "ok", data }))
        .catch((err) =>
          settle({
            state: "error",
            error: err?.message || "Failed to load activity",
          }),
        );
    },
    [api],
  );

  useEffect(() => {
    generation.current += 1;
    limiter.current.clear();
    setSummaries({});
    for (const pixel of page.items) loadSummary(pixel.uuid);
  }, [page.items, loadSummary]);

  useEffect(() => {
    const queue = limiter.current;
    const gen = generation;
    return () => {
      gen.current += 1;
      queue.clear();
    };
  }, []);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const handleOwnerChange = (value) => {
    setOwner(value);
    setCursors([null]);
    setPageIndex(0);
  };

  const handleNext = () => {
    if (!page.nextCursor) return;
    setCursors((prev) => [...prev.slice(0, pageIndex + 1), page.nextCursor]);
    setPageIndex((i) => i + 1);
  };

  const handleSave = async (body) => {
    if (modal?.pixel) {
      await api.update(modal.pixel.uuid, body);
    } else {
      await api.create(body);
    }
    setModal(null);
    refresh();
  };

  const handleDelete = async (pixel) => {
    if (
      !window.confirm(
        `Delete pixel "${pixel.label}"? It will stop collecting data immediately (within 30 seconds).`,
      )
    ) {
      return;
    }
    try {
      await api.remove(pixel.uuid);
      refresh();
    } catch (err) {
      window.alert("Failed to delete pixel: " + (err.message || "Unknown"));
    }
  };

  const colSpan = isAdmin ? 5 : 4;

  return (
    <section>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-base text-(--text-secondary)">
          Collect page-view data from your sites with a script or image pixel.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && (
            <select
              aria-label="Filter by owner"
              value={owner}
              onChange={(e) => handleOwnerChange(e.target.value)}
              className="rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-3 py-2 text-[13px] text-(--text-primary)"
            >
              <option value="">All owners</option>
              {(users ?? []).map((u) => (
                <option key={u.uuid} value={u.uuid}>
                  {u.username}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => setModal({})}
            className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
          >
            <Plus size={16} strokeWidth={2.5} />
            Create pixel
          </button>
        </div>
      </div>

      {error && (
        <Card className="mb-4">
          <p className="text-sm text-(--status-danger)" role="alert">
            {error}
          </p>
        </Card>
      )}

      <DataTable>
        <thead>
          <tr className={DATA_TABLE_CLASSES.headRow}>
            <th className={DATA_TABLE_CLASSES.th}>Label</th>
            {isAdmin && <th className={DATA_TABLE_CLASSES.th}>Owner</th>}
            <th className={DATA_TABLE_CLASSES.th}>Last hit</th>
            <th className={DATA_TABLE_CLASSES.th}>Events 24h</th>
            <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colSpan} className={DATA_TABLE_CLASSES.emptyCell}>
                Loading pixels…
              </td>
            </tr>
          ) : page.items.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className={DATA_TABLE_CLASSES.emptyCell}>
                No pixels yet
              </td>
            </tr>
          ) : (
            page.items.map((pixel) => (
              <tr
                key={pixel.uuid}
                className={`${DATA_TABLE_CLASSES.bodyRow} cursor-pointer`}
                onClick={() => navigate(`${basePath}/pixels/${pixel.uuid}`)}
              >
                <td className={DATA_TABLE_CLASSES.td}>
                  <div className="flex items-center gap-3">
                    <RowIcon icon={Radio} />
                    <span className="font-semibold">{pixel.label}</span>
                  </div>
                </td>
                {isAdmin && (
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {pixel.ownerUsername ??
                      (users ?? []).find((u) => u.uuid === pixel.ownerUserUuid)
                        ?.username ??
                      "—"}
                  </td>
                )}
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                >
                  <SummaryCell
                    summary={summaries[pixel.uuid]}
                    label={`last hit for ${pixel.label}`}
                    onRetry={() => loadSummary(pixel.uuid)}
                    render={(s) =>
                      s.lastHitAt ? formatDateTime(s.lastHitAt) : "Never"
                    }
                  />
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                >
                  <SummaryCell
                    summary={summaries[pixel.uuid]}
                    label={`events for ${pixel.label}`}
                    onRetry={() => loadSummary(pixel.uuid)}
                    render={(s) => (
                      <span
                        title={`${formatCount(s.hits24h)} page views · ${formatCount(s.beacons24h)} events (last 24–25h)`}
                      >
                        {formatCount(s.beacons24h)}
                      </span>
                    )}
                  />
                </td>
                <td
                  className={DATA_TABLE_CLASSES.tdRight}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ActionMenu
                    ariaLabel={`Actions for ${pixel.label}`}
                    items={[
                      {
                        label: "Edit",
                        icon: Pencil,
                        onClick: () => setModal({ pixel }),
                      },
                      {
                        label: "Delete",
                        icon: Trash2,
                        danger: true,
                        onClick: () => handleDelete(pixel),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </DataTable>

      {(pageIndex > 0 || page.nextCursor) && (
        <div className="mt-4 flex items-center justify-end gap-3">
          <span className="text-[12px] text-(--text-muted)">
            Page {pageIndex + 1}
          </span>
          <Button
            variant="secondary"
            size="small"
            disabled={pageIndex === 0 || loading}
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="small"
            disabled={!page.nextCursor || loading}
            onClick={handleNext}
          >
            Next
          </Button>
        </div>
      )}

      <Modal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        title={modal?.pixel ? "Edit pixel" : "Create pixel"}
        size="small"
      >
        {modal !== null && (
          <PixelForm
            key={modal.pixel?.uuid ?? "new"}
            initialPixel={modal.pixel}
            onSubmit={handleSave}
            onCancel={() => setModal(null)}
          />
        )}
      </Modal>
    </section>
  );
}
